import type { CastProfile } from './castProfiles.ts'
import type { CastProfileTimeline } from './castProfileTimelines.ts'

const PERSON_ID = 'p-carmella-tyray-arc'
const B90 = '90 Day Fiancé: Before the 90 Days'

// This stable provisional catalog ID indexes an impersonation storyline, not
// a verified Barbadian girlfriend or a full biography of the photograph owner.
// Keep its false romance metadata out of the editorial links and origin hero.
// Provenance: research/bio-research-final-carmella-2026-10-01.json.
export const finalCarmellaProfiles: CastProfile[] = [{
  personId: PERSON_ID,
  entryKind: 'story_identity',
  headline: 'An impersonated online identity—not a four-year romance with the woman pictured.',
  bio: [
    '“Carmella” needs an identity check before a couple hashtag. Tyray entered Season 6 believing he had spent four years dating a woman in Barbados through Snapchat. Producers identified a man named Christian as the person behind those messages. The online girlfriend, the man operating the account and the real woman in its photographs were not the same person. This page explains that storyline; it does not assign the impersonator’s promises or claimed background to the woman whose pictures were used.',
    'The woman described on the show as the real Carmella, and in later reporting as Sweet Carmella, agreed to meet Tyray in Denver. She said she did not know Christian or participate in the deception, and she encouraged Tyray to stay positive. His thoughtful candle arrived; the imagined four-year relationship did not. In a September 2023 interview, Tyray said they never spoke again after that meeting and had not pursued a relationship. Her legal name, personal history and present-day dating life are not verified here.',
  ],
  originStory: 'This entry began with the online “Carmella” persona in Tyray’s Before the 90 Days Season 6 story. Production identified Christian as the impersonator. The separate woman whose photographs had been used later appeared when Tyray met her in Denver for answers and closure; she was not his long-distance girlfriend.',
  journeys: [{
    label: 'Where it started: an impersonated identity',
    era: 'Before the 90 Days · Season 6',
    summary: 'Tyray’s online romance involved a false persona using someone else’s pictures. His later meeting with the photograph owner was a search for closure, not a reunion with the person who had sent the messages.',
  }],
  sources: [
    { label: 'TLC: Season 6 introduction and its identity warning', url: 'https://www.tlc.com/shows/90-day-fiance-before-the-90-days/photo-galleries/90-day-fiance-before-the-90-days-couples-bios' },
    { label: 'ET: Tyray’s direct interview about the impersonation', url: 'https://www.etonline.com/90-day-fiances-tyray-responds-to-those-who-say-he-willingly-got-catfished-and-should-date-women-on' },
    { label: 'ET: The separate woman Tyray met in Denver', url: 'https://www.etonline.com/90-day-fiance-recap-tyray-finally-meets-the-real-carmella-209559' },
    { label: 'Us Weekly: Tyray’s September 2023 follow-up interview', url: 'https://www.usmagazine.com/entertainment/news/90-days-tyray-mollett-reveals-whether-he-speaks-to-carmella/' },
  ],
}]

export const finalCarmellaTimelines: Record<string, CastProfileTimeline> = {
  [PERSON_ID]: {
    origin: {
      partnerName: 'Tyray’s catfishing storyline',
      introLabel: 'An impersonated identity in Tyray’s story',
      connectionKind: 'unverified-identity',
      show: B90,
      season: 6,
      // Deliberately no partnerPersonId: the current status helper must not
      // match this identity-context origin to the catalog's false couple arc.
      // Tyray has a direct profile link in connections and the season footer.
    },
    connections: [{
      name: 'Tyray Mollett',
      personId: 'p-tyray-mollett',
      context: 'The cast member who received the impersonator’s messages and later met the separate photograph owner. She was not his online girlfriend, and he said in September 2023 that they did not pursue a relationship.',
    }, {
      name: 'Christian (the impersonator)',
      context: 'The name production identified behind the false online persona. A verified surname and separate catalog person ID are not established here; no link to Christian Allgood from Cleo’s Season 6 story is inferred.',
    }],
    seasons: [{
      show: B90,
      season: 6,
      status: 'aired',
      title: 'Separating the persona from the photograph owner',
      recap: 'Production revealed that a man had operated the online “Carmella” identity. Tyray later met the woman whose photographs had been used in Denver; she denied involvement, and their meeting offered closure rather than a continuing romance.',
      partnerPersonIds: ['p-tyray-mollett'],
      // No relationshipId: do not generate an Open their couple journey link.
    }],
  },
}
